import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  inject,
} from '@angular/core';
import {
  RouteKnowledge,
  GeoLocation,
  RegisteredRoute,
  RouteQualification,
  UserQualificationV2WithQ,
} from '@wilson/interfaces';
import { MapboxToken } from '@wilson/mapbox-config';
import { add, isBefore } from 'date-fns';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';
import * as mapboxgl from 'mapbox-gl';
import {
  railwayLinesLayer,
  userQualificationRouteLinesLayer,
  userQualificationRouteLinesSelectionLayer,
  userQualificationRoutePointLabelLayer,
  userQualificationRoutePointsLayer,
} from './route-qualification-map-source-layer';

@Component({
  selector: 'wilson-route-qualification-map-view',
  templateUrl: './route-qualification-map-view.component.html',
  styleUrls: ['./route-qualification-map-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RouteQualificationMapViewComponent implements AfterViewInit {
  private readonly mapboxToken = inject(MapboxToken);
  private readonly cd = inject(ChangeDetectorRef);

  @Input() routeQualifications:
    | (UserQualificationV2WithQ & { route: RegisteredRoute })[]
    | RouteQualification[]
    | RouteKnowledge[] = [];

  map!: mapboxgl.Map;

  selectedRouteQualification:
    | ((RouteQualification | RouteKnowledge) & { name: string })
    | null = null;

  ngAfterViewInit(): void {
    if (this.routeQualifications.length > 0) {
      this.loadMap();
    }
  }

  loadMap(): void {
    this.map = new mapboxgl.Map({
      accessToken: this.mapboxToken,
      container: 'map',
      style: 'mapbox://styles/m79/clgvqhype007l01qy2yeugeqm',
      zoom: 6,
      center: [9.763831, 50.994266],
    });
    this.map.on('load', () => {
      this.renderRailwayLines();
      this.renderRoutes();
      this.map.resize();
    });
    this.map.addControl(new mapboxgl.NavigationControl());

    this.map.on('click', 'userQualiRouteLinesSelection', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['userQualiRouteLinesSelection'],
      });

      if (!features.length) {
        return;
      }

      const feature = features[0];
      const qualification = feature?.properties
        ? feature?.properties['qualification']
        : null;
      if (!qualification) return;

      const parsed = JSON.parse(qualification);
      this.selectedRouteQualification = {
        ...parsed,
        name: this.isExpandedRoute(parsed) ? parsed.name : parsed.route.name,
      };
      this.cd.detectChanges();
    });
  }

  /**
   * Type guard to check if the input is a RouteKnowledge (new endpoint format)
   * RouteKnowledge has validTill at the root level and is the route itself
   */
  private isExpandedRoute(
    item:
      | (UserQualificationV2WithQ & { route: RegisteredRoute })
      | RouteQualification
      | RouteKnowledge,
  ): item is RouteKnowledge {
    return 'startLocationId' in item && !('route' in item);
  }

  renderRoutes(): void {
    const routeGeoJsonFeatures: Feature<Geometry, GeoJsonProperties>[] = [];

    for (const qualification of this.routeQualifications) {
      const coordinates = [];

      const route = this.isExpandedRoute(qualification)
        ? qualification
        : qualification.route;
      const validTill = qualification.validTill;

      if (!route.startLocation || !route.endLocation) {
        continue;
      }

      const routeLayerColor = this.getLineColorFromValidationStatus(validTill);

      if (route.startLocation) {
        const locationCoordinates = this.addCoordinates(route.startLocation);
        coordinates.push(locationCoordinates);
        routeGeoJsonFeatures.push(
          this.getRoutePoint(
            locationCoordinates,
            route.startLocation,
            routeLayerColor,
          ),
        );
      }
      route.viaLocations?.forEach((viaLocation: GeoLocation) => {
        if (viaLocation) {
          const locationCoordinates = this.addCoordinates(viaLocation);
          coordinates.push(locationCoordinates);
          routeGeoJsonFeatures.push(
            this.getRoutePoint(
              locationCoordinates,
              viaLocation,
              routeLayerColor,
            ),
          );
        }
      });
      if (route.endLocation) {
        const locationCoordinates = this.addCoordinates(route.endLocation);
        coordinates.push(locationCoordinates);
        routeGeoJsonFeatures.push(
          this.getRoutePoint(
            locationCoordinates,
            route.endLocation,
            routeLayerColor,
          ),
        );
      }

      const tripData: Feature<Geometry, GeoJsonProperties> = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates,
        },
        properties: {
          lineColor: routeLayerColor,
          qualification: qualification,
        },
      };

      routeGeoJsonFeatures.push(tripData);
    }

    this.map.addSource('userQualiRoutes', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: routeGeoJsonFeatures,
      },
    });
    this.map.addLayer(userQualificationRouteLinesLayer);
    this.map.addLayer(
      userQualificationRouteLinesSelectionLayer,
      'userQualiRouteLines',
    );
    this.map.addLayer(userQualificationRoutePointsLayer, 'userQualiRouteLines');
    this.map.addLayer(userQualificationRoutePointLabelLayer);
  }

  private renderRailwayLines(): void {
    this.map.addSource('railway-lines', {
      type: 'vector',
      url: 'mapbox://planemad.railway-lines',
    });
    this.map.addLayer(railwayLinesLayer, 'settlement-subdivision-label');
  }

  private getLineColorFromValidationStatus(validTill: string): string {
    return isBefore(new Date(validTill), new Date())
      ? '#eb1f10'
      : isBefore(new Date(validTill), add(new Date(), { months: 3 }))
        ? '#d4c117'
        : '#32a852';
  }

  private addCoordinates(location: GeoLocation): number[] {
    if (Number(location?.longitude) !== 0 && Number(location?.latitude) !== 0) {
      return [Number(location.longitude), Number(location.latitude)];
    } else {
      return [];
    }
  }

  private getRoutePoint(
    coordinates: number[],
    location: GeoLocation,
    layerColor: string,
  ): Feature<Geometry, GeoJsonProperties> {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: coordinates,
      },
      properties: {
        circleColor: layerColor,
        location: location,
        name:
          `${location.name}` + location.locationCode
            ? `(${location.locationCode})`
            : '',
      },
    };
  }
}
